import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { acceptTicketTransferApi } from "../../api/api";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { jwtDecode } from "jwt-decode";
import { LOGOUT_SUCCESS } from "../../redux/actions/action";

const TransferAccept = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const token = searchParams.get("token");

  const [status, setStatus] = useState("idle"); // idle | loading | success | error | mismatch
  const [message, setMessage] = useState("");
  const [intendedEmail, setIntendedEmail] = useState("");

  // Decode token to show toEmail (helps users pick the right account)
  useEffect(() => {
    if (!token) return;
    try {
      const payload = jwtDecode(token);
      const email = (payload?.toEmail || payload?.email || "").toLowerCase();
      if (email) setIntendedEmail(email);
    } catch {}
  }, [token]);

  useEffect(() => {
    // Validate query param first
    if (!token) {
      setStatus("error");
      setMessage("Link de reclamo inválido o incompleto.");
      return;
    }

    // Require authentication; if missing, store token and send to login
    if (!auth?.isAuthenticated || !auth?.accessToken) {
      try {
        localStorage.setItem("pendingTransferToken", token);
      } catch {}
      navigate("/login", { replace: true });
      return;
    }

    // If logged-in email doesn't match token target, ask to switch
    if (intendedEmail && (auth?.user?.email || "").toLowerCase() !== intendedEmail) {
      setStatus("mismatch");
      setMessage("Este link fue enviado a otra cuenta.");
      return;
    }

    const run = async () => {
      try {
        setStatus("loading");
        await acceptTicketTransferApi(auth.accessToken, token);
        setStatus("success");
        setMessage("¡Listo! El ticket ya es tuyo.");
      } catch (err) {
        console.error(err);
        const msg = err?.response?.data?.message || "No se pudo completar el reclamo.";
        setMessage(msg);
        setStatus("error");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, intendedEmail, auth?.isAuthenticated, auth?.accessToken]);

  const onSwitchAccount = () => {
    try { localStorage.setItem("pendingTransferToken", token || ""); } catch {}
    try { dispatch({ type: LOGOUT_SUCCESS }); } catch {}
    navigate("/login", { replace: true });
  };

  if (status === "loading") return <SpinLoading />;

  return (
    <div className="container pading150" style={{ minHeight: "60vh" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        {status === "success" ? (
          <>
            <h2 style={{ marginBottom: 12 }}>Transferencia aceptada</h2>
            <p style={{ opacity: 0.9 }}>{message}</p>
            <div style={{ marginTop: 24 }}>
              <button className="btn btn-primary" onClick={() => navigate("/myevents", { replace: true })}>
                Ver mis eventos
              </button>
            </div>
          </>
        ) : status === "mismatch" ? (
          <>
            <h2 style={{ marginBottom: 12 }}>Iniciá sesión con la cuenta correcta</h2>
            <p style={{ opacity: 0.9 }}>
              Este link es para: <b>{intendedEmail || "(desconocido)"}</b>
            </p>
            <p style={{ opacity: 0.9 }}>
              Estás conectado como: <b>{auth?.user?.email}</b>
            </p>
            <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "center" }}>
              <button className="btn btn-primary" onClick={onSwitchAccount}>
                Cambiar de cuenta
              </button>
              <button className="btn btn-secondary" onClick={() => navigate("/", { replace: true })}>
                Volver al inicio
              </button>
            </div>
          </>
        ) : status === "error" ? (
          <>
            <h2 style={{ marginBottom: 12 }}>No se pudo reclamar el ticket</h2>
            <p style={{ opacity: 0.9 }}>{message}</p>
            <div style={{ marginTop: 24 }}>
              <button className="btn btn-secondary" onClick={() => navigate("/", { replace: true })}>
                Volver al inicio
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Procesando transferencia…</h2>
            <p>Por favor, espera un momento.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default TransferAccept;
